import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next';
import { getCheckProfilePayload, getCheckProfileUser, getCookie, hasCheckProfileUser } from '../utils/profileRedirect'
import api from '../config/api';
import {
  resolveSecondLoginFlow,
  setJourneyFlowStage,
} from '../utils/journeyFlow';
import { redirectToRoute } from '../utils/routeRedirect';


const trackInteraction = async (action, value = null) => {
  try {
    await api.post(
      "/interaction/track",
      {
        action,
        value,
      },
      {
        withCredentials: true,
      }
    );
  } catch (error) {
    console.error("Interaction tracking failed:", error);
  }
};

function WelcomeBack() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const clearAllClientStorage = () => {
    const domains = ["",
      ".ilovebritannia.com",
      "ilovebritannia.com",
      ".thirukkural.milkbikis.ilovebritannia.com",
      "thirukkural.milkbikis.ilovebritannia.com",
    ];

    const paths = ["/", "/register", "/login", "/home", "/welcome-back"];
    const expired = "expires=Thu, 01 Jan 1970 00:00:01 GMT";

    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0].trim();
      if (!name) return;

      // Delete without domain
      paths.forEach((path) => {
        document.cookie = `${name}=; ${expired}; path=${path};`;
      });

      // Delete with each domain + path combo (with and without Secure flag)
      domains.forEach((domain) => {
        if (!domain) return;
        paths.forEach((path) => {
          document.cookie = `${name}=; ${expired}; path=${path}; domain=${domain};`;
          document.cookie = `${name}=; ${expired}; path=${path}; domain=${domain}; Secure;`;
        });
      });
    });

    localStorage.clear();
    sessionStorage.clear();
  };

  const clearEverything = async () => {
    try {
      await api.post(
        "/user/logout",
        {},
        {
          withCredentials: true,
        }
      );
      // await api.post("/user/logout");
    } catch (err) {
      console.warn("Backend cleanup failed:", err);
    }

    // Remove all JS-accessible cookies
    const domains = [
      window.location.hostname,
      "." + window.location.hostname,
      ".ilovebritannia.com",
      "ilovebritannia.com",
      ".thirukkural.milkbikis.ilovebritannia.com",
      "thirukkural.milkbikis.ilovebritannia.com",
      "api.thirukkural.milkbikis.ilovebritannia.com",
    ];

    const paths = [
      "/",
      "/register",
      "/login",
      "/home",
      "/welcome-back",
    ];

    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0].trim();

      if (!name) return;

      paths.forEach((path) => {
        // without domain
        document.cookie =
          `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path};`;

        // with domains
        domains.forEach((domain) => {
          document.cookie =
            `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain};`;

          document.cookie =
            `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}; Secure; SameSite=None`;
        });
      });
    });

    // storage cleanup
    localStorage.clear();
    sessionStorage.clear();

    // service worker cache cleanup
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }

    // redirect
    window.location.replace("/register");
  };


  const handleNotMe = async () => {
    setLoading(true);
    await trackInteraction("WELCOME_BACK_NOT_ME");
    await clearEverything();
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profileId = getCookie("profileId");

        if (!profileId) {
          navigate("/login");
          return;
        }

        const user = await getCheckProfileUser(profileId);

        if (hasCheckProfileUser(user)) {
          setUserName(user.name || "");
          setUserPhone(user.mobile || "");
        } else {
          navigate("/login");
        }
      } catch (error) {
        console.log(error);
        navigate("/login");
      }
    };

    fetchProfile();
  }, [navigate]);

  const handleRedirect = async () => {
    await trackInteraction("WELCOME_BACK_CONTINUE");
    try {
      const userDataResponse = await api.get(
        "/user/userData",
        { withCredentials: true }
      );
      const nextFlow = resolveSecondLoginFlow(userDataResponse?.data?.data);
      setJourneyFlowStage(nextFlow.stage);
      redirectToRoute(navigate, nextFlow.route);
    } catch (error) {
      console.error(error);
      navigate("/login");
    }
  };

  const maskPhone = (phone) => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 4) return `+91 ${digits}`;
    const first = digits[0];
    const last3 = digits.slice(-3);
    const masked = first + "X".repeat(digits.length - 4) + last3;
    return `+91 ${masked}`;
  };

  return (
    <>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      <link rel="shortcut icon" href="assets/images/favicon.ico" type="image/x-icon" />
      <link rel="stylesheet" href="assets/css/styles.css?ver=2.2" />
      <title>Welcome Back | Britannia</title>
      <div className="page-wrapper welcome-back-page">
        <div className="main-wrapper yellow-bg">
          <div className="d-frame-outer desktop-only">
            <img src="assets/images/desktop-star.webp" className="d-frame-star-bg" />
            <span className="d-frame-corner tl"></span>
            <span className="d-frame-corner tr"></span>
            <span className="d-frame-corner bl"></span>
            <span className="d-frame-corner br"></span>
            <div className="d-frame-border-outer">
              <div className="d-frame-border">
                <div className="d-frame-border-inner">
                  <div className="d-frame-canvas">
                    <div className="d-frame-content">
                      <div className="desktop-center">
                        <div className="desktop-content-box">
                          <h3>Thank you for your interest in participating in the</h3>
                          <img src="assets/images/desktop-logo-milk-bikis.webp" className="desktop-logo" alt="Milk Bikis Logo" />
                          <div className="desktop-qr-box">
                            <span className="qr-corner qr-corner-tl"></span>
                            <span className="qr-corner qr-corner-tr"></span>
                            <span className="qr-corner qr-corner-bl"></span>
                            <span className="qr-corner qr-corner-br"></span>
                            <div className="qr-left">
                              <p className="qr-text">Please scan the QR code on your phone to enter the experience :</p>
                            </div>
                            <div className="qr-right">
                              <img src="assets/images/desktop-qr.svg" alt="QR Code" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="frame-yellow-bg mobile-only">
            <div className="frame-green-corners">
              <div className="frame-canvas br-16">
                <div className="frame-content">
                  <div className="container-full">
                    <div className="signup-header" style={{ marginTop: "25%" }}>
                      <div className="signup-logo-wrap">
                        <img src="assets/images/animate-britania-logo.webp" alt="Britannia" className="signup-logo signup-logo-1" />
                        <img src="assets/images/animate-milk-bikkis-logo.webp" alt="Milk Bikis" className="signup-logo signup-logo-2" />
                      </div>
                      <img src="assets/images/new-signup-logo.webp" alt="திருக்குறள் சேலஞ்ச்" className="signup-title-img" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="scan-popup">
        <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
        <div className="scan-success-level-1-box">
          <div className="scan-success-level-1-box-scroll">
            <div className="scan-success-level-1-box-inner">
              <div className="star-rotate rot-star-ele">
                <img src="assets/images/cashback-star-element.webp" className="orange-star-bg img-fit" />
              </div>
              <div className="scan-success-level-1-box-text welcome-back-star">
                <div className="cashback-wrapper">
                  <img src="assets/images/welcome-back-icon.webp" className="cashback-icon" />
                </div>
                <p>
                  <span className="fw-700">Continue as {userName || "User"}?</span>
                  <br />
                  {maskPhone(userPhone)}
                </p>
              </div>
            </div>

            <div className="scan-success-level-1-box-footer welcome-back-footer">
              <button
                className="btn-next claim-reward-btn-level-1"
                type="button"
                onClick={handleRedirect}
              >
                <span>Continue</span>
              </button>

              <button
                className="btn-next claim-reward-btn-level-1"
                type="button"
                onClick={handleNotMe}
                disabled={loading}
              >
                <span>{loading ? "Please wait..." : "Not me"}</span>
              </button>

              <Link to="/tnc">{t("common.terms")}</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default WelcomeBack;
